import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, IconButton, Button, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Grid, Tooltip, Checkbox, CircularProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, GetApp as ExportIcon, Search as SearchIcon, Clear as ClearIcon, Category as CategoryIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import { fetchPartAttributes, fetchPartAttributesStats, searchPartAttributes, deletePartAttribute, bulkPartAttributesActivate, bulkPartAttributesDeactivate, bulkPartAttributesDelete, exportPartAttributesCsv, setSearchParams, setSelectedIds, addSelectedId, removeSelectedId, clearSelectedIds, setPagination } from '../../store/reducers/partAttributesSlice';

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(3) },
  statsContainer: { marginBottom: theme.spacing(3) },
  statsCard: { textAlign: 'center' },
  searchContainer: { display: 'flex', gap: theme.spacing(2), marginBottom: theme.spacing(3), alignItems: 'center' },
  tableContainer: { marginBottom: theme.spacing(2) },
  actionButtons: { display: 'flex', gap: theme.spacing(1), marginBottom: theme.spacing(2) },
  statusChip: { minWidth: 80 },
  loadingContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 },
}));

const PartAttributesList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, pagination, statistics, selectedIds, bulkOperationLoading, successMessage } = useSelector((s: RootState) => s.partAttributes);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('all');

  useEffect(() => { dispatch(fetchPartAttributes()); dispatch(fetchPartAttributesStats()); }, [dispatch]);

  const handleSearch = () => {
    const params = { page: 1, pageSize: pagination.pageSize, search: searchTerm, active: activeFilter === 'all' ? undefined : activeFilter === 'true' };
    dispatch(setSearchParams(params));
    dispatch(searchPartAttributes(params));
  };

  const handleClearSearch = () => {
    setSearchTerm(''); setActiveFilter('all');
    const params = { page: 1, pageSize: pagination.pageSize, search: '', active: undefined };
    dispatch(setSearchParams(params));
    dispatch(fetchPartAttributes(params));
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    const params = { page: newPage + 1, pageSize: pagination.pageSize };
    dispatch(setPagination(params));
    dispatch(fetchPartAttributes(params));
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const params = { page: 1, pageSize: parseInt(e.target.value, 10) };
    dispatch(setPagination(params));
    dispatch(fetchPartAttributes(params));
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => { e.target.checked ? dispatch(setSelectedIds(items.map(i => i.Id!))) : dispatch(clearSelectedIds()); };
  const handleSelectItem = (id: number, checked: boolean) => { checked ? dispatch(addSelectedId(id)) : dispatch(removeSelectedId(id)); };
  const handleBulkActivate = () => { if (selectedIds.length) dispatch(bulkPartAttributesActivate(selectedIds)); };
  const handleBulkDeactivate = () => { if (selectedIds.length) dispatch(bulkPartAttributesDeactivate(selectedIds)); };
  const handleBulkDelete = () => { if (selectedIds.length && window.confirm(`Delete ${selectedIds.length} item(s)?`)) dispatch(bulkPartAttributesDelete(selectedIds)); };
  const handleExport = () => { dispatch(exportPartAttributesCsv({ search: searchTerm, active: activeFilter === 'all' ? undefined : activeFilter === 'true' })); };

  const handleEdit = (id: number) => navigate(`/catalogs/partattributes/edit/${id}`);
  const handleDelete = (id: number) => { if (window.confirm('Delete this Part Attribute?')) dispatch(deletePartAttribute(id)); };

  if (loading && items.length === 0) return (<Box className={classes.loadingContainer}><CircularProgress /></Box>);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4">Part Attributes</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/catalogs/partattributes/add')}>Add New Part Attribute</Button>
      </Box>

      {error && (<Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>)}
      {successMessage && (<Alert severity="success" style={{ marginBottom: 16 }}>{successMessage}</Alert>)}

      <Box className={classes.searchContainer}>
        <TextField label="Search" variant="outlined" size="small" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSearch()} style={{ minWidth: 200 }} />
        <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value as string)} label="Status">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" color="primary" startIcon={<SearchIcon />} onClick={handleSearch}>Search</Button>
        <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClearSearch}>Clear</Button>
        <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExport} disabled={loading}>Export CSV</Button>
      </Box>

      {selectedIds.length > 0 && (
        <Box className={classes.actionButtons}>
          <Button variant="contained" color="primary" startIcon={<CheckCircleIcon />} onClick={handleBulkActivate} disabled={bulkOperationLoading}>Activate ({selectedIds.length})</Button>
          <Button variant="contained" color="secondary" startIcon={<CancelIcon />} onClick={handleBulkDeactivate} disabled={bulkOperationLoading}>Deactivate ({selectedIds.length})</Button>
          <Button variant="contained" color="secondary" startIcon={<DeleteIcon />} onClick={handleBulkDelete} disabled={bulkOperationLoading}>Delete ({selectedIds.length})</Button>
        </Box>
      )}

      <Paper className={classes.tableContainer}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox indeterminate={selectedIds.length > 0 && selectedIds.length < items.length} checked={items.length > 0 && selectedIds.length === items.length} onChange={handleSelectAll} />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Part Number</TableCell>
                <TableCell>Part Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.Id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox checked={selectedIds.includes(item.Id!)} onChange={(e) => handleSelectItem(item.Id!, e.target.checked)} />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center"><CategoryIcon style={{ marginRight: 8, fontSize: 20 }} />{item.Name}</Box>
                  </TableCell>
                  <TableCell>{item.Part?.Number || '-'}</TableCell>
                  <TableCell>{item.Part?.Description || '-'}</TableCell>
                  <TableCell>
                    <Chip label={item.Active ? 'Active' : 'Inactive'} color={item.Active ? 'primary' : 'default'} size="small" className={classes.statusChip} />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => handleEdit(item.Id!)}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="secondary" onClick={() => handleDelete(item.Id!)}><DeleteIcon /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={pagination.total} rowsPerPage={pagination.pageSize} page={pagination.page - 1} onPageChange={handlePageChange} onRowsPerPageChange={handlePageSizeChange} />
      </Paper>
    </Box>
  );
};

export default PartAttributesList; 